import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { tenantQuery } from '../../../utils/firestoreHelpers';
import { buildCommissionRecord } from '../../../domain/commissionCalculations';

const COL = 'commission_records';

/**
 * Bill kaydedildikten sonra prim kaydı oluşturur.
 * commissionConfig yoksa sessizce işlem yapılmaz.
 */
export async function createCommissionRecordForBill(bill, commissionConfig, businessId) {
  if (!commissionConfig || !bill.employeeId) return null;
  const record = buildCommissionRecord(bill, commissionConfig, businessId);
  if (record.totalCommission <= 0) return null;
  const ref = await addDoc(collection(db, COL), {
    ...record,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * runTransaction içinde prim kaydı yazar (bill + commission atomik).
 * Sadece yeni bill'lerde (isEditing=false) çağrılır.
 * commissionConfig yoksa veya commission <= 0 ise null döner.
 *
 * @param {import('firebase/firestore').Transaction} tx
 * @param {{ bill: object, commissionConfig: object, businessId: string }} opts
 * @returns {string|null} yeni commission doc ID, ya da null
 */
export function writeCommissionInTx(tx, { bill, commissionConfig, businessId }) {
  if (!commissionConfig || !bill.employeeId) return null;
  const record = buildCommissionRecord(bill, commissionConfig, businessId);
  if (record.totalCommission <= 0) return null;
  const ref = doc(collection(db, COL));
  tx.set(ref, { ...record, createdAt: serverTimestamp() });
  return ref.id;
}

/**
 * Belirli dönem + isteğe bağlı filtreler için prim kayıtlarını getirir.
 *
 * @param {string} businessId
 * @param {string} period  YYYY-MM
 * @param {'all'|'pending'|'paid'|'canceled'} [status]
 */
export async function fetchCommissionsByPeriod(businessId, period, status = 'all') {
  const constraints = [where('period', '==', period), orderBy('billDate', 'desc')];
  if (status !== 'all') constraints.push(where('status', '==', status));
  const { getDocs } = await import('firebase/firestore');
  const q = tenantQuery(collection(db, COL), businessId, ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Seçili prim kayıtlarını toplu olarak "ödendi" yap.
 * status: 'paid' olanlar Firestore rules ile update'e kapalı olacak.
 *
 * @param {string[]} recordIds
 * @param {string} paidByUid
 */
export async function markCommissionsAsPaid(recordIds, paidByUid) {
  if (!recordIds.length) return;
  const { serverTimestamp: ts } = await import('firebase/firestore');
  // Firestore batch: max 500 write/batch
  const chunks = [];
  for (let i = 0; i < recordIds.length; i += 499) {
    chunks.push(recordIds.slice(i, i + 499));
  }
  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach((id) => {
      batch.update(doc(db, COL, id), {
        status: 'paid',
        paidAt: serverTimestamp(),
        paidBy: paidByUid,
      });
    });
    await batch.commit();
  }
}

/**
 * Bill silindiğinde (soft delete) ilgili prim kaydını iptal eder.
 * Nadir event olduğu için transaction dışında ayrı write.
 *
 * @param {string} billId
 * @param {string} businessId
 */
export async function cancelCommissionByBillId(billId, businessId) {
  const { getDocs } = await import('firebase/firestore');
  const q = tenantQuery(
    collection(db, COL),
    businessId,
    where('billId', '==', billId),
    where('status', '==', 'pending')
  );
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.update(d.ref, { status: 'canceled' }));
  if (!snap.empty) await batch.commit();
}
